import { useState, useEffect, useCallback } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import KanbanBoard from "./components/KanbanBoard";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";

interface Project {
  id: string;
  title: string;
  description: string | null;
  color: string;
  status: string;
  created_by: string | null;
  created_at: string;
  task_count?: number;
}

const PROJECT_COLORS = ["#4B6CF7", "#E8375F", "#FF9F43", "#00C896", "#A78BFA", "#EAB308"];

export default function ProjectBoardPage() {
  const { user } = useAuth();
  const { workspaceId } = useCompany();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newColor, setNewColor] = useState(PROJECT_COLORS[0]);
  const [creating, setCreating] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    const { data } = await supabase
      .from("projects")
      .select("*")
      .eq("workspace_id", workspaceId)
      .eq("status", "active")
      .order("created_at", { ascending: false });

    if (data) {
      const { data: taskCounts } = await supabase
        .from("project_tasks")
        .select("project_id")
        .in("project_id", data.map((p: Project) => p.id));
      const countMap: Record<string, number> = {};
      (taskCounts ?? []).forEach((t: { project_id: string }) => { countMap[t.project_id] = (countMap[t.project_id] ?? 0) + 1; });
      const withCounts = data.map((p: Project) => ({ ...p, task_count: countMap[p.id] ?? 0 }));
      setProjects(withCounts);
      if (!selectedProjectId && withCounts.length > 0) setSelectedProjectId(withCounts[0].id);
    }
    setLoading(false);
  }, [workspaceId, selectedProjectId]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreate = async () => {
    if (!user || !workspaceId || !newTitle.trim()) return;
    setCreating(true);
    const { data } = await supabase.from("projects").insert({
      workspace_id: workspaceId,
      title: newTitle.trim(),
      description: newDesc.trim() || null,
      color: newColor,
      created_by: user.id,
    }).select().maybeSingle();
    setCreating(false);
    if (data) {
      setProjects(prev => [{ ...data, task_count: 0 } as Project, ...prev]);
      setSelectedProjectId(data.id);
    }
    setShowCreateModal(false);
    setNewTitle("");
    setNewDesc("");
    setNewColor(PROJECT_COLORS[0]);
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <div className="flex-1 flex overflow-hidden" style={{ height: "calc(100vh - 48px)" }}>
          {/* 프로젝트 사이드바 */}
          <div className="bg-[#111112] border-r border-white/[0.05] flex flex-col shrink-0" style={{ width: 240 }}>
            <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
              <div>
                <div className="text-[#F2F2F2] text-sm font-medium">팀 프로젝트</div>
                <div className="text-[#3D3D3D] text-[10px]">워크스페이스 공유</div>
              </div>
              <button type="button" onClick={() => setShowCreateModal(true)}
                className="w-7 h-7 flex items-center justify-center bg-[#4B6CF7]/15 hover:bg-[#4B6CF7]/25 text-[#7A9BFF] rounded-lg cursor-pointer transition-colors">
                <i className="ri-add-line text-sm"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
              {loading ? [...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-white/[0.02] rounded-xl animate-pulse border border-white/[0.04]" />
              )) : projects.length === 0 ? (
                <div className="text-center py-10">
                  <i className="ri-folder-line text-2xl text-[#222222] block mb-2"></i>
                  <p className="text-[#3D3D3D] text-xs">프로젝트가 없어요</p>
                  <button type="button" onClick={() => setShowCreateModal(true)}
                    className="mt-2 text-[10px] text-[#4B6CF7] hover:text-[#7A9BFF] cursor-pointer transition-colors">
                    + 새 프로젝트 만들기
                  </button>
                </div>
              ) : (
                projects.map(proj => (
                  <button key={proj.id} type="button" onClick={() => setSelectedProjectId(proj.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
                      selectedProjectId === proj.id ? "border-white/[0.12] bg-white/[0.06]" : "border-transparent hover:bg-white/[0.03] hover:border-white/[0.06]"
                    }`}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: proj.color }}></div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium truncate ${selectedProjectId === proj.id ? "text-[#F2F2F2]" : "text-[#AAAAAA]"}`}>
                          {proj.title}
                        </p>
                        {proj.description && (
                          <p className="text-[#3D3D3D] text-[9px] truncate mt-0.5">{proj.description}</p>
                        )}
                      </div>
                      <span className="text-[9px] text-[#444444] shrink-0">{proj.task_count}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* 칸반 보드 */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#0D0D0F]">
            {!selectedProject ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/[0.03] border border-white/[0.05]">
                  <i className="ri-kanban-view-2 text-3xl text-[#333333]"></i>
                </div>
                <div className="text-center">
                  <p className="text-[#555555] text-sm">프로젝트를 선택하거나 새로 만드세요</p>
                  <button type="button" onClick={() => setShowCreateModal(true)}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#4B6CF7] text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-[#5B7CF7] transition-colors whitespace-nowrap">
                    <i className="ri-add-line text-sm"></i>새 프로젝트 만들기
                  </button>
                </div>
              </div>
            ) : (
              <KanbanBoard project={selectedProject} workspaceId={workspaceId ?? ""} onUpdate={fetchProjects} />
            )}
          </div>
        </div>
      </div>

      {/* 프로젝트 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setShowCreateModal(false)}>
          <div className="bg-[#1C1F2E] rounded-2xl border border-white/[0.08] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-[#F2F2F2] font-bold text-base mb-4 flex items-center gap-2">
              <i className="ri-folder-add-line text-[#4B6CF7]"></i>새 프로젝트 만들기
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-[#666666] text-xs mb-1 block">프로젝트 이름</label>
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} maxLength={50}
                  placeholder="예: 루나&스타즈 2nd EP 프로젝트"
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#4B6CF7]/40 placeholder-[#3A3A3A]" />
              </div>
              <div>
                <label className="text-[#666666] text-xs mb-1 block">설명 (선택)</label>
                <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} maxLength={200}
                  placeholder="프로젝트 목표나 간단한 설명" rows={2}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm px-3 py-2.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#4B6CF7]/40 placeholder-[#3A3A3A] resize-none" />
              </div>
              <div>
                <label className="text-[#666666] text-xs mb-2 block">프로젝트 색상</label>
                <div className="flex gap-2 flex-wrap">
                  {PROJECT_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setNewColor(c)}
                      className="w-7 h-7 rounded-full cursor-pointer transition-all border-2"
                      style={{ backgroundColor: c, borderColor: newColor === c ? "#FFFFFF" : "transparent", transform: newColor === c ? "scale(1.2)" : "scale(1)" }} />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-[#666666] text-sm hover:text-[#888888] cursor-pointer transition-colors whitespace-nowrap">취소</button>
                <button type="button" onClick={handleCreate} disabled={!newTitle.trim() || creating}
                  className="flex-1 py-2.5 rounded-xl bg-[#4B6CF7] text-white text-sm font-semibold hover:bg-[#5B7CF7] cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50">
                  {creating ? "생성 중..." : "프로젝트 생성"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
